import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { getHashBasedNumber } from '@rivethealth/data/hash-based-number';
import { AvatarSize, AvatarState } from '@rivethealth/data/options';
import { ReplaySubject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

// This must correspond to the number of variants in ./avatar.component.scss:
const TOTAL_VARIANTS = 26;

@Component({
  selector: 'avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
  @Input('avatarState')
  set inputAvatarState(value: AvatarState | null) {
    if (value != null) {
      this.avatarState.next(value);
    }
  }

  @Input()
  useInitials: boolean = true;

  avatarState = new ReplaySubject<AvatarState>(1);

  sizeClass = this.avatarState.pipe(
    filter(state => state != null),
    map(state => {
      switch (state!.size) {
        case AvatarSize.XSMALL:
          return 'xsmall';
        case AvatarSize.SMALL:
          return 'small';
        case AvatarSize.MEDIUM:
          return 'medium';
      }
    }),
  );

  fallbackVariantClass = this.avatarState.pipe(
    filter(
      (state): state is AvatarState & { name: string } =>
        state != null && state.name != null,
    ),
    map(state =>
      getHashBasedNumber(state.name.trim().toLocaleLowerCase(), TOTAL_VARIANTS),
    ),
    map(v => `variant-${v}`),
    startWith(''),
  );

  displayText = this.avatarState.pipe(
    filter(state => !!state?.name),
    map(state => {
      const name = state!.name!.trim();
      if (!this.useInitials) {
        return name;
      }
      return name
        .replace(/[^a-zA-Z\s]/g, '')
        .split(' ')
        .filter((_, i, arr) => i === 0 || i === arr.length - 1)
        .map(part => part[0])
        .join('');
    }),
    startWith(null),
  );
}
